import {
  Button,
  Container,
  FormField,
  Header,
  Input,
  Select,
  SpaceBetween
} from '@cloudscape-design/components';
import { OptionDefinition } from '@cloudscape-design/components/internal/components/option/interfaces';
import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../context/authContext';
import { AccountFeatureFlag, DOCTYPE } from '../../lib/models/interfaces';

const COLLAPSED_DESCRIPTION_STYLES = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical' as const,
  WebkitLineClamp: 3,
  overflow: 'hidden'
};

export const ProjectForm = (props: {
  createNewProject: (
    projectName: string,
    projectDescription: string,
    projectType: string
  ) => void;
  setFolderModal: (arg: boolean) => void;
  projectType?: string | null;
  origin?: string;
}) => {
  const [projectName, setProjectName] = useState<string>('');
  const [projectDescription, setProjectDescription] = useState<string>('');
  const [projectType, setProjectType] = useState<OptionDefinition>({
    label: 'Workspace',
    value: 'workspace'
  });
  const [expandedDescriptions, setExpandedDescriptions] = useState<
    Record<string, boolean>
  >({});
  const { companyAccount } = useContext(UserContext);

  useEffect(() => {
    if (props?.projectType) {
      switch (true) {
        case props?.projectType === 'information_request':
          setProjectType({
            label: 'Information Request',
            value: 'information_request'
          });
          break;
        case props?.projectType === 'workspace':
          setProjectType({
            label: 'Workspace',
            value: 'workspace'
          });
          break;
        case props?.projectType === DOCTYPE.policy_document:
          setProjectType({
            label: 'Policy Document',
            value: DOCTYPE.policy_document
          });
          break;
        case props?.projectType === DOCTYPE.contract:
          setProjectType({
            label: 'Contract',
            value: DOCTYPE.contract
          });
          break;
      }
    }
    if (props?.origin === 'gap') {
      setProjectType({
        label: 'Information Request',
        value: 'information_request'
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const projectOptions = [
    {
      title: 'Document Project',
      value: 'workspace',
      description:
        'Upload and analyze your internal documents. Extract requirements, summarize information and create AI findings. ',
      targetFlag: 'analysis'
    },
    {
      title: 'Questionnaire Project',
      value: 'information_request',
      description:
        'Import spreadsheet questionnaires and let AI help you complete it. Use for vendor questionnaires, customer requests, audits and assessments.',
      targetFlag: 'rfp'
    },
    {
      title: 'Questionnaire Library Project',
      value: 'information_request_library',
      description:
        'Import previous questionnaires and let AI identify the questions and answers. Both spreadsheet and document formats can be uploaded.',
      targetFlag: 'rfp'
    },
    {
      title: 'Policy Document Project',
      value: 'policy_document',
      description: 'Import  / Create Policy documents to compare against.',
      targetFlag: 'contract'
    },
    {
      title: 'Contract Document Project',
      value: 'contract',
      description: 'Upload Contacts to evaluate',
      targetFlag: 'contract'
    }
  ];
  const visibleProjectOptions = projectOptions.filter((option) =>
    companyAccount?.accountFlags?.includes(option.targetFlag as AccountFeatureFlag)
  );
  const hasMultipleProjectOptions = visibleProjectOptions.length > 1;
  const cardWidthRem = 16;
  const cardGapRem = 1;
  const cardRowWidth = hasMultipleProjectOptions
    ? `${cardWidthRem * 2 + cardGapRem}rem`
    : `${cardWidthRem}rem`;
  const formContentWidth = props.origin
    ? '32rem'
    : hasMultipleProjectOptions
      ? cardRowWidth
      : '24rem';

  const handleReset = () => {
    setProjectDescription('');
    setProjectName('');
    setProjectType({
      label: 'Workspace',
      value: 'workspace'
    });
  };

  const toggleDescription = (value: string) => {
    setExpandedDescriptions((prev) => ({
      ...prev,
      [value]: !prev[value]
    }));
  };

  return (
    <div className="mx-auto inline-block max-w-[calc(100vw-2rem)]">
      <Container
        header={<Header>Create New Project</Header>}
        footer={
          <SpaceBetween size={'xs'} direction="horizontal">
            <Button onClick={() => props.setFolderModal(false)}>Cancel</Button>
            <Button
              disabled={!projectName || !projectType.value}
              onClick={() => {
                props.createNewProject(
                  projectName,
                  projectDescription,
                  projectType.value!
                );
                // handleReset();
              }}
            >
              Create Project
            </Button>
          </SpaceBetween>
        }
      >
        <div
          style={{
            width: formContentWidth,
            maxWidth: 'calc(100vw - 8rem)'
          }}
        >
          <SpaceBetween size={'xs'}>
            <FormField label="Project Name">
              <Input
                value={projectName}
                onChange={(event) => setProjectName(event.detail.value)}
              />
            </FormField>
            <FormField label="Project Description - Optional">
              <Input
                value={projectDescription}
                onChange={(event) => setProjectDescription(event.detail.value)}
              />
            </FormField>
            {!props.origin && (
              <>
                <p className="text-2xl text-center">Select Type</p>
                <div
                  className="flex max-w-full flex-wrap items-stretch justify-center gap-4"
                  style={{ width: cardRowWidth }}
                >
                  {visibleProjectOptions.map((option) => {
                    const isSelected = projectType.value === option.value;
                    const isExpanded = Boolean(
                      expandedDescriptions[option.value]
                    );
                    const shouldShowToggle = option.description.length > 90;
                    return (
                      <div
                        key={option.value}
                        className="flex w-full sm:w-64 sm:flex-none"
                      >
                        <div
                          role="button"
                          tabIndex={0}
                          aria-pressed={isSelected}
                          onClick={() =>
                            setProjectType({
                              label: option.title,
                              value: option.value
                            })
                          }
                          onKeyDown={(event) => {
                            if (
                              event.key === 'Enter' ||
                              event.key === ' '
                            ) {
                              event.preventDefault();
                              setProjectType({
                                label: option.title,
                                value: option.value
                              });
                            }
                          }}
                          className={`flex h-full w-full cursor-pointer flex-col gap-2 rounded-lg border p-4 text-left shadow-sm transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                            isSelected
                              ? 'border-blue-600 ring-2 ring-blue-100 bg-blue-50'
                              : 'border-gray-200 bg-white'
                          }`}
                        >
                          <p className="min-h-[2.5rem] text-base font-semibold text-gray-900">
                            {option.title}
                          </p>
                          <p
                            className="text-sm text-gray-700"
                            style={
                              isExpanded
                                ? undefined
                                : COLLAPSED_DESCRIPTION_STYLES
                            }
                          >
                            {option.description}
                          </p>
                          {shouldShowToggle ? (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                toggleDescription(option.value!);
                              }}
                              className="w-fit text-xs font-medium text-blue-700 hover:text-blue-800"
                            >
                              {isExpanded ? 'Show less' : 'Show more'}
                            </button>
                          ) : null}
                          <p
                            className={`mt-auto inline-flex min-h-[1.25rem] items-center text-sm font-medium ${
                              isSelected ? 'text-blue-700' : 'text-gray-600'
                            }`}
                          >
                            {isSelected ? 'Selected' : 'Select this option'}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
            {props?.origin === 'gap' && (
              <div>
                <p>
                  The destination folder for Gap analysis is a Questionnaire
                  project.
                </p>
              </div>
            )}
          </SpaceBetween>
        </div>
      </Container>
    </div>
  );
};
